import { FileText, Loader2 } from "lucide-react";
import { API_ROOT_PATH } from "../apiHelper";

const InvoicePDFTemplate = ({ invoice, loading }) => {
    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount, currency = 'NGN') => {
        if (amount == null) return '—';
        return new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: currency || 'NGN',
            minimumFractionDigits: 2
        }).format(amount);
    };

    // Dynamic status based on due date
    const getPaymentStatus = () => {
        if (!invoice?.creditsale?.payment_due_date) return null;

        const dueDate = new Date(invoice.creditsale.payment_due_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        dueDate.setHours(0, 0, 0, 0);

        if (dueDate < today) {
            return { label: 'Overdue', color: 'text-red-600' };
        }
        if (dueDate.getTime() === today.getTime()) {
            return { label: 'Due Today', color: 'text-orange-600' };
        }
        const daysLeft = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
        return { label: `Due in ${daysLeft} day${daysLeft > 1 ? 's' : ''}`, color: 'text-orange-600' };
    };

    const paymentStatus = getPaymentStatus();

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 size={40} className="animate-spin text-gmail-blue" />
            </div>
        );
    }

    if (!invoice) {
        return (
            <div className="text-center py-20 text-gray-500">
                <FileText size={64} className="mx-auto mb-6 text-gray-300" />
                <p>Invoice details not available.</p>
            </div>
        );
    }

    const subtotal = invoice.totalproduct || 0;
    const discount = invoice.totaldiscount || 0;
    const tax = invoice.totaltax || 0;
    const total = subtotal - discount + tax;

    return (
        <div
            className="bg-white shadow-xl rounded-lg overflow-hidden font-sans antialiased"
            style={{
                fontFamily: "'Open Sans', 'WebFont-Open Sans', system-ui, -apple-system, sans-serif",
                printColorAdjust: 'exact',
                WebkitPrintColorAdjust: 'exact'
            }}
        >
            <div className="w-full" style={{ aspectRatio: '1 / 1.414', maxWidth: '210mm', margin: '0 auto' }}>
                <div className="p-12 h-full flex flex-col text-gray-800">

                    {/* Header / Letterhead */}
                    {invoice.store?.invoice_header ? (
                        <div className="mb-8 border-b pb-6">
                            <img
                                src={`${API_ROOT_PATH}/${invoice.store.invoice_header}`}
                                alt="Store Invoice Header"
                                className="w-full max-h-36 object-contain rounded-md"
                            />
                            <div className="flex justify-between items-center mt-4">
                                <p className="text-3xl font-bold text-gmail-blue">
                                    #{invoice.idempotency_key}
                                </p>
                                <span className="text-xs uppercase font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full tracking-wider">
                                    INVOICE
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-between items-start mb-12">
                            <div>
                                <h1 className="text-5xl font-bold">INVOICE</h1>
                                <p className="text-3xl font-bold text-gmail-blue mt-3">
                                    #{invoice.idempotency_key}
                                </p>
                            </div>
                            <div className="text-right">
                                <h2 className="text-2xl font-bold">{invoice.organization?.organization_name || 'Your Store'}</h2>
                                <p className="text-sm mt-1">{invoice.store?.identifier}</p>
                            </div>
                        </div>
                    )}

                    {/* Bill To & Invoice Info */}
                    <div className="grid grid-cols-2 gap-12 mb-12">
                        <div>
                            <p className="font-bold text-lg mb-3">Bill To:</p>
                            <p className="font-semibold text-lg">
                                {invoice.customer?.user ? `${invoice.customer.user.first_name || ''} ${invoice.customer.user.middle_name || ""} ${invoice.customer.user.last_name || ''}`.trim() : 'Valued Customer'}
                            </p>
                            {invoice.customer?.user?.first_name && (
                                <p className="text-gray-600">{invoice.customer.user.first_name} {invoice.customer.user.middle_name || ""} {invoice.customer.user.last_name}</p>
                            )}
                        </div>
                        <div className="text-right space-y-2">
                            <p><span className="font-bold">Invoice Date:</span> {formatDate(invoice.placed_on)}</p>
                            {invoice.creditsale?.payment_due_date && (
                                <p><span className="font-bold">Due Date:</span> {formatDate(invoice.creditsale.payment_due_date)}</p>
                            )}
                            {paymentStatus && (
                                <p>
                                    <span className="font-bold">Status:</span>{' '}
                                    <span className={`font-bold ${paymentStatus.color}`}>
                                        {paymentStatus.label}
                                    </span>
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Line Items */}
                    <table className="w-full mb-12 text-sm">
                        <thead>
                            <tr className="border-b-2 border-gray-800 text-left">
                                <th className="pb-4 font-bold">Description</th>
                                <th className="pb-4 font-bold text-center">Qty</th>
                                <th className="pb-4 font-bold text-right">Unit Price</th>
                                <th className="pb-4 font-bold text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoice.orderitems?.length > 0 ? (
                                invoice.orderitems.map((item, i) => (
                                    <tr key={item.orderitems_id || i} className="border-b border-gray-200">
                                        <td className="py-4 font-medium text-gray-900">
                                            {item.catentry?.productname || 'N/A'}
                                        </td>
                                        <td className="py-4 text-center">{item.quantity}</td>
                                        <td className="py-4 text-right">{formatCurrency(item.price, item.currency)}</td>
                                        <td className="py-4 text-right font-medium">
                                            {formatCurrency((item.totalproduct || item.price * item.quantity), item.currency)}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={4} className="py-8 text-center text-gray-500">No items</td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* Totals */}
                    <div className="ml-auto w-80 mt-auto">
                        <div className="space-y-3 text-lg">
                            <div className="flex justify-between">
                                <span>Subtotal:</span>
                                <span>{formatCurrency(subtotal, invoice.currency)}</span>
                            </div>
                            {discount > 0 && (
                                <div className="flex justify-between text-green-600">
                                    <span>Discount:</span>
                                    <span>-{formatCurrency(discount, invoice.currency)}</span>
                                </div>
                            )}
                            {tax > 0 && (
                                <div className="flex justify-between">
                                    <span>Tax:</span>
                                    <span>{formatCurrency(tax, invoice.currency)}</span>
                                </div>
                            )}
                            <div className="flex justify-between pt-4 border-t-2 border-gray-800 text-2xl font-bold">
                                <span>Total Due:</span>
                                <span className="text-gmail-blue">{formatCurrency(total, invoice.currency)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Dynamic Footer */}
                    {invoice.creditsale?.payment_due_date ? (
                        <div className="mt-12 pt-8 border-t border-gray-300 text-center text-sm text-gray-600">
                            <p className="font-medium">Thank you for your business!</p>
                            <p className="mt-2">
                                Payment is due by <span className="font-bold">{formatDate(invoice.creditsale.payment_due_date)}</span>.
                                Please contact us if you have any questions.
                            </p>
                        </div>
                    ) : (
                        <div className="mt-12 pt-8 border-t border-gray-300 text-center text-sm text-gray-600">
                            <p className="font-medium">Thank you for your business!</p>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default InvoicePDFTemplate;